/**
 * travelExpensesAccess — geteilte Zugriffslogik für die Dienstreisen-/
 * Reisekostenerfassung (RK-7) UND ihren Auslands-Sub-Flag (RK-14/RK-15,
 * architecture-reisekosten.md §13.7).
 *
 * Single source of truth dafür, WER die Reisekosten-Seite sehen und benutzen
 * darf. Wird von `dashboardRoutePaths.ts` (Route-Registrierung) und
 * `TravelExpensesPage` (Inhalts-Gate) gemeinsam genutzt — kein Copy-Paste der
 * Flag-/Allowlist-Bedingung. Muster: `config/receiptScanAccess.ts` (§3.7
 * architecture-reisekosten.md).
 *
 * Zwei Freischalt-Pfade (fail-closed — Default ist KEIN Zugriff):
 *   1. Beta-Allowlist: explizit gelistete Test-Account-UIDs sehen die Seite
 *      LIVE, unabhängig vom Tier und vom Env-Flag (kontrollierte Erprobung).
 *   2. Allgemeine Freigabe: `REACT_APP_TRAVEL_EXPENSES_ENABLED='true'` UND Ultra-Tier.
 *
 * Pricing-/Tier-Kopplung (GA-Freischaltung 2026-07-26, Betreiber-Ansage Daniel,
 * modules-ga-freischaltung.md §3): Reisekosten wird von Premium auf **Ultra**
 * hochgezogen — gleichgestellt zu Belegscanner/Bank-Abgleich/DATEV/UStVA. Deshalb
 * wechselt die Signatur von `isPremium: boolean` auf `tier: Tier` und der Term
 * lautet `FLAG ∧ tier === 'ultra'`. (Der frühere O-6-Stand „bleibt Premium",
 * 2026-07-14, ist durch die neuere GA-Ansage überholt.) KEIN Take-away/Grace
 * nötig: das Feature war für Premium-User nie live (Flag war nie an — nur Beta-UID).
 *
 * Zusätzlich (RK-14/§13.7, NEU 2026-07-12): ein zweites, UNABHÄNGIGES Sub-Flag-Gate
 * für den Auslands-Zweig — {@link canUseForeignTravel} setzt das Basis-Feature
 * ({@link canUseTravelExpenses}) IMMER voraus (Ausland nie ohne Inland, E12) und
 * hat eine eigene Beta-Allowlist + ein eigenes Env-Flag. {@link getForeignRatesForUser}
 * ist der EINE Entscheidungs-Helfer (V19 Gate-Parity): sowohl die Live-Vorschau
 * (`TripSettlementSummary`) als auch `settleTravelTrip` beziehen die Auslands-Sätze
 * AUSSCHLIESSLICH über diesen Helfer — nie über eine eigene, potenziell abweichende
 * Gate-Logik (sonst das bekannte „Vorschau ok, Abrechnen failt"-Anti-Pattern).
 *
 * @module travelExpensesAccess
 */

import type { TravelForeignRatesConfig } from '../utils/travel/travelRates';
import { TRAVEL_RATES_FOREIGN } from '../utils/travel/travelRatesForeign.generated';
import type { Tier } from './entitlements';

/**
 * Beta-Erprobung Reisekosten — entfernen/erweitern, wenn allgemein freigegeben.
 *
 * Nur diese UIDs erhalten Zugriff unabhängig vom Premium-Status und vom Env-Flag.
 * HARTKODIERT für die kontrollierte Live-Erprobung (§3.7). Der `travel_trips`-
 * Rules-Block muss deployt sein, BEVOR diese Liste nicht-leer live geht
 * (Deploy-Sequencing, architecture-reisekosten.md §8/modules-reisekosten.md RK-4).
 */
export const TRAVEL_EXPENSES_BETA_UIDS: readonly string[] = ['b3DRR1Cj63cRjdJg5VCEUpBL7hk1'];

/**
 * Allgemeines Feature-Flag. Fail-closed: nur bei explizitem `'true'` aktiv.
 * Modul-Konstante, einmalig beim Bundle-Load ausgewertet.
 */
export const TRAVEL_EXPENSES_FLAG: boolean = process.env.REACT_APP_TRAVEL_EXPENSES_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob ein Nutzer die Reisekosten-Seite sehen/benutzen darf.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined,
 *              solange noch nicht eingeloggt).
 * @param tier - effektiver Tier des Nutzers (`resolveTier(sellerData)` aus
 *               `entitlements.ts`). Reisekosten ist ab GA ein Ultra-Feature.
 * @returns `true`, wenn die UID auf der Beta-Allowlist steht ODER das Flag aktiv
 *          ist UND der Nutzer den Ultra-Tier hat. Sonst `false`.
 */
export function canUseTravelExpenses(uid: string | null | undefined, tier: Tier): boolean {
  if (uid && TRAVEL_EXPENSES_BETA_UIDS.includes(uid)) {
    return true;
  }
  return TRAVEL_EXPENSES_FLAG && tier === 'ultra';
}

// ─── Auslands-Sub-Flag (RK-14/RK-15, architecture-reisekosten.md §13.7) ────────

/**
 * Beta-Erprobung Auslands-Zweig — SEPARAT von {@link TRAVEL_EXPENSES_BETA_UIDS}, damit die
 * Basis-Reisekostenerfassung und der (auslegungsintensivere, RECOMMEND-HUMAN-LAWYER-behaftete)
 * Auslands-Zweig unabhängig voneinander erprobt/freigeschaltet werden können (E12).
 */
export const TRAVEL_FOREIGN_BETA_UIDS: readonly string[] = ['b3DRR1Cj63cRjdJg5VCEUpBL7hk1'];

/**
 * Auslands-Sub-Feature-Flag. Fail-closed: nur bei explizitem `'true'` aktiv. Unabhängig von
 * {@link TRAVEL_EXPENSES_FLAG} — GA des Sub-Flags erst nach O-11 (StB-/Jurist-Abnahme) + O-13
 * (SKR-Konto Auslandsverpflegung), s. modules-reisekosten.md „Go-Live-Checkliste".
 */
export const TRAVEL_FOREIGN_FLAG: boolean = process.env.REACT_APP_TRAVEL_FOREIGN_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob ein Nutzer den AUSLANDS-Zweig der Reisekostenerfassung
 * sehen/benutzen darf (§13.7 E12). Sub-Gate: setzt {@link canUseTravelExpenses} IMMER
 * voraus — der Auslands-Zweig existiert nie ohne die Basis-Reisekostenerfassung.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined).
 * @param tier - effektiver Tier des Nutzers (`resolveTier(sellerData)`).
 * @returns `true`, wenn die Basis-Freigabe vorliegt UND (die UID auf der Auslands-Beta-Allowlist
 *          steht ODER das Auslands-Flag aktiv ist UND der Nutzer den Ultra-Tier hat). Sonst `false`.
 */
export function canUseForeignTravel(uid: string | null | undefined, tier: Tier): boolean {
  if (!canUseTravelExpenses(uid, tier)) {
    return false;
  }
  if (uid && TRAVEL_FOREIGN_BETA_UIDS.includes(uid)) {
    return true;
  }
  return TRAVEL_FOREIGN_FLAG && tier === 'ultra';
}

/**
 * DER EINE Entscheidungs-Helfer für die Auslands-Sätze-Übergabe (V19 Gate-Parity,
 * architecture-reisekosten.md §13.7/§13.11). Liefert die Auslands-Konfiguration GENAU DANN,
 * wenn {@link canUseForeignTravel} offen ist — sonst `undefined`.
 *
 * PFLICHT: sowohl die Live-Vorschau (`TripSettlementSummary` → `calculateTravelAllowances`)
 * als auch `settleTravelTrip` beziehen `options.foreignRates` AUSSCHLIESSLICH über diesen
 * Helfer — nie über eine eigene Gate-Prüfung. Andernfalls könnten Vorschau und Abrechnen
 * unterschiedliche Entscheidungen treffen (bekanntes Hook-/Gate-Parity-Anti-Pattern).
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined).
 * @param tier - effektiver Tier des Nutzers (`resolveTier(sellerData)`).
 * @returns Das Auslands-Sätze-Asset (`TRAVEL_RATES_FOREIGN`), wenn das Gate offen ist,
 *          sonst `undefined` — wird 1:1 als `options.foreignRates` an
 *          `calculateTravelAllowances` durchgereicht (weggelassen/`undefined` ⇒
 *          byte-identisches Inlands-MVP-Verhalten, Soll-Paar #40).
 */
export function getForeignRatesForUser(
  uid: string | null | undefined,
  tier: Tier,
): TravelForeignRatesConfig | undefined {
  return canUseForeignTravel(uid, tier) ? TRAVEL_RATES_FOREIGN : undefined;
}

// ─── Route-Lookup-Sub-Flag (RK-19/RK-20/RK-21, architecture-reisekosten.md §14.1 E20) ─────────

/**
 * Beta-Erprobung Distanz-Ermittlung (Route-Lookup) — SEPARAT von {@link TRAVEL_EXPENSES_BETA_UIDS}
 * und {@link TRAVEL_FOREIGN_BETA_UIDS} (Muster E12): die externe-API-Anbindung (openrouteservice)
 * hat ein eigenes Rechts-Gate und wurde unabhängig vom Basis-Feature und vom Auslands-Zweig
 * erprobt. Identische Beta-UID wie {@link TRAVEL_FOREIGN_BETA_UIDS} (kontrollierte Live-Erprobung
 * desselben Test-Accounts).
 *
 * 🔴 IST-STAND 2026-07-29 — HIER LIEGT EINE OFFENE RECHTSBEDINGUNG, KEINE ALTE NOTIZ:
 * Das Feature ist seit PR #317 **GA** ({@link TRAVEL_ROUTE_FLAG} live `true`, damit für ALLE
 * Ultra-Kunden offen, nicht mehr nur für die Beta-UID). Die O-16-Freigabe des Juristen war aber
 * eine **Beta**-Freigabe unter aufschiebender Bedingung, und deren Bedingung (e)1 — schriftliche
 * HeiGIT-Auskunft zu Serverstandort, Drittlandübermittlung, Logging und AVV-Angebot (Operator-
 * Aktion **O-1**, offen seit 2026-07-13) — ist bis heute **nicht erfüllt**. Für GA lag zu keinem
 * Zeitpunkt eine Freigabe vor (`plan-feedback-jurist-route-ga-wiedervorlage.md` Abschnitt 1).
 *
 * Erledigt (Transparenz-Ebene, Nachfrist 2026-08-12): A-8 Datenschutzerklärung ohne die
 * unbelegte Drittland-Aussage, A-9 dauerhafter Übermittlungshinweis vor der Erhebung
 * (`mileage.routeLookupNotice`). **Weiterhin offen und beim Betreiber:** O-1 (HeiGIT-Auskunft),
 * O-2 (AVV-Empfängerklausel), O-3 (VVT-Eintrag), O-4 (TIA, bedingt auf O-1), O-5 (ORS-Self-
 * Hosting bewerten). Der Jurist verlangt ausdrücklich KEIN Zurückflippen des Flags; wer dieses
 * Gate anfasst, prüft aber zuerst den Stand von O-1..O-5.
 */
export const TRAVEL_ROUTE_BETA_UIDS: readonly string[] = ['b3DRR1Cj63cRjdJg5VCEUpBL7hk1'];

/**
 * Route-Lookup-Feature-Flag. Fail-closed: nur bei explizitem `'true'` aktiv. Unabhängig von
 * {@link TRAVEL_EXPENSES_FLAG}/{@link TRAVEL_FOREIGN_FLAG}. Client-seitig NUR das UI-Gate —
 * die Callable selbst hat ZUSÄTZLICH ein eigenes serverseitiges Gate (env-Flag + Secret-
 * Präsenz, `functions/src/travel/routeDistance.ts`, RK-20), weil das Client-Flag im Bundle
 * steht und jeder authentifizierte Nutzer die Callable sonst direkt aufrufen könnte (E20).
 */
export const TRAVEL_ROUTE_FLAG: boolean = process.env.REACT_APP_TRAVEL_ROUTE_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob ein Nutzer den Route-Lookup (Start/Ziel-Adressen → Distanz-
 * Vorschlag) sehen/benutzen darf (§14.1 E20). Sub-Gate: setzt {@link canUseTravelExpenses}
 * IMMER voraus — Route-Lookup existiert nie ohne die Basis-Reisekostenerfassung (Muster E12).
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined).
 * @param tier - effektiver Tier des Nutzers (`resolveTier(sellerData)`).
 * @returns `true`, wenn die Basis-Freigabe vorliegt UND (die UID auf der Route-Beta-Allowlist
 *          steht ODER das Route-Flag aktiv ist UND der Nutzer den Ultra-Tier hat). Sonst `false`.
 */
export function canUseRouteLookup(uid: string | null | undefined, tier: Tier): boolean {
  if (!canUseTravelExpenses(uid, tier)) {
    return false;
  }
  if (uid && TRAVEL_ROUTE_BETA_UIDS.includes(uid)) {
    return true;
  }
  return TRAVEL_ROUTE_FLAG && tier === 'ultra';
}
